import React, { ChangeEvent, Dispatch, SetStateAction, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { Person } from '@mui/icons-material';
import { Autocomplete, Box, Button, Checkbox, FormControlLabel, IconButton, Stack, TextField } from '@mui/material';

import { queryParamNames } from '~/components/Promo/hooks/usePromoCodes';

import useCurrentUser from '~/hooks/useCurrentUser';
import useQuery from '~/hooks/useQuery';

import { GetPromoCodeProps, PromoTag, PromoUserRole, getPromoMeta, promoRoles } from '~/services/PromoService';

import { getPromoTagArrays } from '~/utils/promoTags';

import Spinner from '../Spinner';
import UserSearchWidget from '../UserSearchWidget';

interface Props {
  searchQuery: (query: GetPromoCodeProps) => void;
  isLoading: boolean;
}

function PromoSearchForm({ searchQuery, isLoading }: Props) {
  const query = useQuery();
  const { enqueueSnackbar } = useSnackbar();
  const brand = useSelector((state: App.State) => state.tenant.brand);
  const { user: currentUser } = useCurrentUser();

  const [promoCodeName, setPromoCodeName] = useState(query.get(queryParamNames.name));
  const [caseId, setCaseId] = useState(query.get(queryParamNames.caseId));

  const [userIdInteracted, setUserIdInteracted] = useState(query.get(queryParamNames.userIdInteracted));
  const [userSearchValue, setUserSearchValue] = useState<string>('');

  const [promoEmailFilter, setPromoEmailFilter] = useState(query.get(queryParamNames.emailAddress));
  const [expiresAfterToday, setExpiresAfterToday] = useState<boolean>(
    query.get(queryParamNames.expiresAfter) != 'false',
  );
  const [noChildPromos, setNoChildPromos] = useState<boolean>(query.get(queryParamNames.noChildPromos) == 'true');
  const [noReferralPromos, setNoReferralPromos] = useState<boolean>(
    query.get(queryParamNames.excludeReferralPromos) == 'true',
  );
  const [excludeApprovedPromos, setExcludeApprovedPromos] = useState<boolean>();

  const [promoTagsLoading, setPromoTagsLoading] = useState<boolean>(true);
  const [promoTags, setPromoTags] = useState<Array<PromoTag>>(null);
  const [promoDeptTagOptions, setPromoDeptTagOptions] = useState<Array<string>>([]);
  const [promoDeptTag, setPromoDeptTag] = useState<string>(query.get(queryParamNames.deptTag));
  const [promoCategoryTagOptions, setPromoCategoryTagOptions] = useState<Array<string>>([]);
  const [promoCategoryTag, setPromoCategoryTag] = useState<string>(query.get(queryParamNames.categoryTag));
  const [promoSubCategoryTagOptions, setPromoSubCategoryTagOptions] = useState<Array<string>>([]);
  const [promoSubCategoryTag, setPromoSubCategoryTag] = useState<string>(query.get(queryParamNames.subCategoryTag));

  const [promoUserRoles, setPromoUserRoles] = useState<Array<PromoUserRole>>([]);
  const [isDev, setIsDev] = useState<boolean>(query.get(queryParamNames.dev) == 'true');

  // Add "Only mine" toggle state
  const [onlyMine, setOnlyMine] = useState<boolean>(false);

  const [showAdvanced, setShowAdvanced] = useState<boolean>(
    Boolean(promoDeptTag) ||
      Boolean(promoCategoryTag) ||
      Boolean(promoSubCategoryTag) ||
      Boolean(noChildPromos) ||
      Boolean(noReferralPromos) ||
      Boolean(caseId) ||
      Boolean(userIdInteracted),
  );

  const history = useHistory();

  const handleSubmit = (event) => {
    event.preventDefault();

    // Set userIdInteracted to current user's ID if "Only mine" is enabled
    const finalUserIdInteracted = onlyMine ? currentUser?.memberId || '' : userIdInteracted || '';

    const query: GetPromoCodeProps = {
      codeName: promoCodeName || '',
      page: 1,
      limit: 10,
      brand: brand,
      caseId: caseId || '',
      userIdInteracted: finalUserIdInteracted,
      emailAddress: promoEmailFilter || '',
      expiresAfter: expiresAfterToday ? new Date().toISOString() : '',
      noChildPromos: !!noChildPromos,
      excludeReferralPromos: noReferralPromos,
      excludeApprovedPromos: excludeApprovedPromos,
      deptTag: promoDeptTag || '',
      categoryTag: promoCategoryTag || '',
      subCategoryTag: promoSubCategoryTag || '',
      dev: isDev,
    };
    searchQuery(query);
  };

  const onChange = (setState: Dispatch<SetStateAction<string>>) => (event: ChangeEvent<HTMLInputElement>) => {
    setState(event.target.value);
  };

  const handleUserIdInteractedChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setUserIdInteracted(newValue);
    if (onlyMine) {
      setOnlyMine(false);
    }
  };

  const handleUserSearchSelect = (id: string, userId: string) => {
    setUserIdInteracted(userId);
    if (onlyMine) {
      setOnlyMine(false);
    }
  };

  const handleClearAll = (event) => {
    event.preventDefault();
    setPromoCodeName('');
    setPromoEmailFilter('');
    setCaseId('');
    setExpiresAfterToday(true);
    setNoChildPromos(false);
    setNoReferralPromos(false);
    setPromoDeptTag('');
    setPromoCategoryTag('');
    setPromoSubCategoryTag('');
    setIsDev(false);
    setOnlyMine(false);
    setUserIdInteracted('');
    setUserSearchValue('');
    history.push({ search: null });
  };

  // Handle "Only mine" toggle change
  const handleOnlyMineChange = () => {
    const newOnlyMineState = !onlyMine;
    setOnlyMine(newOnlyMineState);

    if (newOnlyMineState) {
      // Set userIdInteracted to current user's ID and trigger search
      const currentUserId = currentUser?.memberId || '';
      setUserIdInteracted(currentUserId);
      setUserSearchValue(currentUser?.email || '');

      // Trigger search with the new filter
      const query: GetPromoCodeProps = {
        codeName: promoCodeName || '',
        page: 1,
        limit: 10,
        brand: brand,
        caseId: caseId || '',
        userIdInteracted: currentUserId,
        emailAddress: promoEmailFilter || '',
        expiresAfter: expiresAfterToday ? new Date().toISOString() : '',
        noChildPromos: !!noChildPromos,
        excludeReferralPromos: noReferralPromos,
        excludeApprovedPromos: excludeApprovedPromos,
        deptTag: promoDeptTag || '',
        categoryTag: promoCategoryTag || '',
        subCategoryTag: promoSubCategoryTag || '',
        dev: isDev,
      };
      searchQuery(query);
    } else {
      // Clear userIdInteracted when "Only mine" is disabled
      setUserIdInteracted('');
      setUserSearchValue('');

      // Trigger search with cleared userIdInteracted
      const query: GetPromoCodeProps = {
        codeName: promoCodeName || '',
        page: 1,
        limit: 10,
        brand: brand,
        caseId: caseId || '',
        userIdInteracted: '',
        emailAddress: promoEmailFilter || '',
        expiresAfter: expiresAfterToday ? new Date().toISOString() : '',
        noChildPromos: !!noChildPromos,
        excludeReferralPromos: noReferralPromos,
        excludeApprovedPromos: excludeApprovedPromos,
        deptTag: promoDeptTag || '',
        categoryTag: promoCategoryTag || '',
        subCategoryTag: promoSubCategoryTag || '',
        dev: isDev,
      };
      searchQuery(query);
    }
  };

  const handleGoToPromo = () => {
    if (!promoCodeName) {
      enqueueSnackbar('Please enter a promo code name');
      return;
    }
    history.push(`/promos/code/${promoCodeName}`);
  };

  const refreshMetaOptions = (promoTags: Array<PromoTag>, selectedDepartment: string, selectedCategory: string) => {
    const tagArrays = getPromoTagArrays({ promoTags, selectedDepartment, selectedCategory, usersPromoRoles: null });
    setPromoDeptTagOptions(tagArrays.departmentTags);
    setPromoCategoryTagOptions(tagArrays.categoryTags);
    setPromoSubCategoryTagOptions(tagArrays.subCategoryTags);
    if (tagArrays.categoryTags.length == 1) {
      setPromoCategoryTag(tagArrays.categoryTags[0]);
    } else if (tagArrays.categoryTags.length == 0) {
      setPromoCategoryTag('');
    }
    if (tagArrays.subCategoryTags.length == 1) {
      setPromoSubCategoryTag(tagArrays.subCategoryTags[0]);
    } else if (tagArrays.subCategoryTags.length == 0) {
      setPromoSubCategoryTag('');
    }
  };

  useEffect(() => {
    if (promoTags !== null) {
      return;
    }
    setPromoTagsLoading(true);
    getPromoMeta()
      .then((res) => {
        setPromoTags(res.result.promo_tags);
        setPromoUserRoles(res.result.promo_roles_for_user);
      })
      .catch((e) => {
        enqueueSnackbar(e.message);
      })
      .finally(() => {
        setPromoTagsLoading(false);
      });
  }, [promoTags, enqueueSnackbar]);

  const searchAll = () => {
    setPromoCodeName('ALL');
    handleSubmit(new Event('submit', { cancelable: true }));
  };

  useEffect(() => {
    if (promoTags != null) {
      refreshMetaOptions(promoTags, promoDeptTag, promoCategoryTag);
    }
  }, [promoTags, promoDeptTag, promoCategoryTag]);

  // Effect to uncheck "Only mine" when userIdInteracted changes from external sources
  useEffect(() => {
    const currentUserId = currentUser?.memberId || '';
    if (onlyMine && userIdInteracted !== currentUserId) {
      setOnlyMine(false);
    }
  }, [userIdInteracted, onlyMine, currentUser?.memberId]);

  const isSearching =
    isLoading &&
    (promoCodeName !== '' || userIdInteracted !== '' || caseId !== '' || promoEmailFilter !== '' || onlyMine);
  const isClearable =
    promoCodeName ||
    userIdInteracted ||
    caseId ||
    promoEmailFilter ||
    expiresAfterToday ||
    noChildPromos ||
    promoDeptTag ||
    promoCategoryTag ||
    promoSubCategoryTag ||
    excludeApprovedPromos ||
    onlyMine;

  return (
    <form onSubmit={handleSubmit}>
      <Stack direction="column" spacing={2}>
        <Stack direction="row" spacing={2} mt={2}>
          <TextField
            label="Promo code name"
            placeholder="Promo code name"
            value={promoCodeName}
            onChange={onChange(setPromoCodeName)}
            fullWidth
          />
          <Button
            variant="contained"
            onClick={handleGoToPromo}
            className="T-promo-search-button"
            type="submit"
            disabled={isSearching || !promoCodeName || promoCodeName === 'ALL'}
          >
            Show
          </Button>
          <Button variant="contained" className="T-promo-search-button" type="submit" disabled={isSearching}>
            {isSearching ? 'Searching…' : 'Search'}
          </Button>
        </Stack>
        <Stack direction="row" spacing={2} mt={2}>
          <Box sx={{ flex: 1 }}>
            <UserSearchWidget
              id="UserIdInteracted"
              label="Filter by User ID Interacted"
              formErrors={[]}
              loading={isSearching}
              onChange={handleUserSearchSelect}
              passedAutocompleteValue={userSearchValue}
              setPassedAutocompleteValue={setUserSearchValue}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              onClick={handleOnlyMineChange}
              disabled={isSearching}
              color={onlyMine ? 'primary' : 'default'}
              title="Show only promo codes that you have interacted with (requested, used, logged, or created)"
              size="small"
            >
              <Person />
            </IconButton>
          </Box>
        </Stack>
        <Stack direction="row">
          <FormControlLabel
            control={
              <Checkbox
                checked={!!expiresAfterToday}
                onChange={onChange(() => setExpiresAfterToday(!expiresAfterToday))}
              />
            }
            label="Hide Expired"
            title="Only return Promo Codes that have not expired"
          />
          {promoUserRoles.map((pr) => pr.roleName).includes(promoRoles.PROMO_BETA_TESTER_ROLE) && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={excludeApprovedPromos}
                  onChange={onChange(() => setExcludeApprovedPromos(!excludeApprovedPromos))}
                />
              }
              label="Hide Approved"
              title="Only return Promo Codes that have not been approved"
            />
          )}
        </Stack>
        <Stack direction="row" mt={2}>
          {promoTagsLoading ? <Spinner /> : null}
          <Box sx={{ flex: 1 }}>
            <Autocomplete
              value={promoDeptTag}
              options={promoDeptTagOptions}
              renderInput={(params) => <TextField {...params} variant="outlined" label="Department" />}
              onChange={(e: unknown, newValue: string | null) => {
                setPromoDeptTag(newValue);
                setPromoCategoryTag(null);
                setPromoSubCategoryTag(null);
              }}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Autocomplete
              value={promoCategoryTag}
              options={promoCategoryTagOptions}
              renderInput={(params) => <TextField {...params} variant="outlined" label="Category" />}
              onChange={(e: unknown, newValue: string | null) => {
                setPromoCategoryTag(newValue);
                setPromoSubCategoryTag(null);
              }}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Autocomplete
              value={promoSubCategoryTag}
              options={promoSubCategoryTagOptions}
              renderInput={(params) => <TextField {...params} variant="outlined" label="Sub Category" />}
              onChange={(e: unknown, newValue: string | null) => {
                setPromoSubCategoryTag(newValue);
              }}
            />
          </Box>
        </Stack>
        <Box>
          <Button variant="text" onClick={() => setShowAdvanced(!showAdvanced)}>
            Advanced Options
          </Button>
        </Box>
        {showAdvanced && (
          <Stack direction="column">
            <Stack direction="row" spacing={2}>
              <TextField
                id="CaseIdSearch"
                label="Case Id"
                placeholder="Case Id"
                value={caseId}
                onChange={onChange(setCaseId)}
                disabled={isSearching}
                fullWidth
              />
              <TextField
                label="Promo Email Restriction"
                placeholder="Filter by email address restriction"
                title="Filter by email address restriction (note: does not currently include promo codes used by a specific user, only promo codes that are restricted to a specific email address)"
                value={promoEmailFilter}
                onChange={onChange(setPromoEmailFilter)}
                fullWidth
              />
              <DummyButton isSearching={isSearching} />
            </Stack>
            <Stack direction="row" spacing={2} mt={2}>
              <FormControlLabel
                control={
                  <Checkbox checked={!!noChildPromos} onChange={onChange(() => setNoChildPromos(!noChildPromos))} />
                }
                label="Hide child promos"
                title="Hide Promo Codes that have been cloned from another promo (i.e. child promos)"
              />
              <FormControlLabel
                control={<Checkbox checked={!!isDev} onChange={onChange(() => setIsDev(!isDev))} />}
                label="Is Dev"
                title="Check this box to show dev fields and features"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!noReferralPromos}
                    onChange={onChange(() => setNoReferralPromos(!noReferralPromos))}
                  />
                }
                label="Exclude Referral Promo"
                title="Check this box to hide Promo Codes that are the result of a referral event"
              />
            </Stack>
            <Stack direction="row" spacing={2} mt={2}>
              <Button variant="contained" className="T-promo-search-button" disabled={isSearching} onClick={searchAll}>
                Search all
              </Button>
              {isClearable && (
                <Button variant="text" onClick={handleClearAll} disabled={isLoading}>
                  Clear All
                </Button>
              )}
            </Stack>
          </Stack>
        )}
      </Stack>
    </form>
  );
}

function DummyButton({ isSearching }) {
  return (
    <Button variant="contained" className="T-promo-search-button" style={{ visibility: 'hidden' }}>
      {isSearching ? 'Searching…' : 'Search'}
    </Button>
  );
}

export default PromoSearchForm;
